/**
 * SAP Ship Doc — the quarterly shipping document, read out of SAP instead of assembled by VLOOKUP.
 *
 * WHAT IT REPLACES. docs/HƯỚNG DẪN CHUẨN BỊ FILE SAP.docx: export ZMMR00100, repair the date column
 * with Text-to-Columns, build the helper key "SFP Code & EXF date", VLOOKUP the Item Seq, pivot to
 * prove the split quantities add back up to the PO quantity, highlight duplicate item numbers by eye.
 * Every step of that is a workaround for something SAP already records — see sapShipDoc.js on the
 * server, which reads the link out of RESB and carries the measurements.
 *
 * TWO OWNERS, ONE ROW, and the screen has to say which is which. Everything from Sales' Invoice
 * rightwards is typed in after the goods arrive (Max, 2026-09-11: "điền sau lúc nhận hàng") and a sync
 * never touches it; everything to its left is SAP's and is rewritten on every sync. The Columns menu
 * marks the two with different dots, the same way Capacity does.
 *
 * ONE NUMBER IS A PROPOSAL, and it is flagged rather than presented as fact. Where a material was
 * bought in several tranches on one PO, which consuming ITEM line belongs to which tranche is not
 * recorded anywhere in SAP — measured on Q1 2026, that is 70% of rows. The allocation this module
 * makes reconciles perfectly against the guide's own test, and so does the one Quang made by hand, and
 * they still disagree. So Item Seq. 1 shows a mark when SAP did not settle it, and can be typed over.
 *
 * Typed IN THE ROW, not in a dialog: the neighbouring lines are the reason somebody is reading this
 * document at all. Same decision as Capacity.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, AlertTriangle, Pencil, FilterX, Columns3, Check, X, Pin,
  Download, DownloadCloud, CircleAlert, ChevronDown, ListChecks,
} from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import i18n from './i18n';
import Header from './Header';
import useAuth from './useAuth';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import { BACKEND_URL } from './backendConfig';
import useRowWindow from './useRowWindow';
import useFillHeight from './useFillHeight';
import { SourceStep, SeqStep, QtyStep, DestinationStep, needsSfp, needsItem, qtyGroups } from './36_ShipDocSteps';
import useFrozenColumns from './useFrozenColumns';
import ChangeCheckDialog from './ChangeCheckDialog';
import ModuleHelp from './ModuleHelp';
import './SPHub.css';
/* The portal's shared skin for a windowed, frozen-column table. Named for the screen it was written
   for; copying 270 lines to rename the prefix would leave two stylesheets to keep in step. */
import './Capacity.css';
import './SapShipDoc.css';

const API = `${BACKEND_URL}/api/production/sap-ship-doc`;
const ROW_H = 34;
const NO_W = 52;
const ACT_W = 74;

/* Declared widths: the table is windowed, and with an automatic layout the browser re-measures the
   columns against a different slice of rows on every scroll step.
   `lk` is the heading's dictionary key (xShipDoc, 2026-10-21), read with t() where the heading is drawn —
   the column KEY is what the code, the store and the server read. */
const COLUMNS = [
  { key: 'vendorName', lk: 'xShipDoc.colVendor', w: 150 },
  { key: 'destination', lk: 'xShipDoc.colDestination', w: 110 },
  /* 180, not 130 — Max, 2026-09-21: "tăng width lên thêm 1 nữa". Measured rather than eyeballed:
     the longest customer PO in the data is 22 characters ("2603-HINC-Costco Chair"), which needs
     about 155px of text plus the cell's own padding. The table's width is the SUM of its columns,
     so widening one widens the table and the frozen offsets follow it — there is no second number
     to keep in step. */
  { key: 'customerPo', lk: 'xShipDoc.colCustomerPo', w: 180 },
  { key: 'exfDate', lk: 'xShipDoc.colExfDate', w: 100 },
  { key: 'sfpPo', lk: 'xShipDoc.colSfpPo', w: 108 },
  { key: 'sfpSeq', lk: 'xShipDoc.colSfpSeq', w: 92, type: 'text' },
  { key: 'sfpCode', lk: 'xShipDoc.colSfpCode', w: 96 },
  { key: 'itemPo', lk: 'xShipDoc.colItemPo', w: 108 },
  { key: 'itemSeq', lk: 'xShipDoc.colItemSeq', w: 92, type: 'text' },
  { key: 'sapCode', lk: 'xShipDoc.colSapCode', w: 96 },
  /* QUANG'S CHECKING COLUMN (2026-10-20): his sheet carries =SAP Code & " & " & Q'ty & " & " & EXF Date
     and highlights a value that appears twice — the same SAP line counted on two rows. Worked out
     here rather than stored, so it can never disagree with the three cells it is made of. */
  { key: 'checking', lk: 'xShipDoc.colChecking', w: 210, get: (r) => checkingOf(r) },
  { key: 'description', lk: 'xShipDoc.colDescription', w: 230 },
  { key: 'color', lk: 'xShipDoc.colColor', w: 140 },
  { key: 'qty', lk: 'xShipDoc.colQty', w: 80, num: true },
  { key: 'unitPrice', lk: 'xShipDoc.colUnitPrice', w: 88, num: true, type: 'number', manualKey: 'unitPriceManual' },
  { key: 'amount', lk: 'xShipDoc.colAmount', w: 100, num: true },
  { key: 'surcharge', lk: 'xShipDoc.colSurcharge', w: 96, num: true, type: 'number' },
  { key: 'salesInvoice', lk: 'xShipDoc.colSalesInvoice', w: 130, type: 'text' },
  { key: 'inboundDelivery', lk: 'xShipDoc.colInboundDelivery', w: 140, type: 'text' },
  { key: 'blAwb', lk: 'xShipDoc.colBlAwb', w: 170, type: 'text' },
  { key: 'checkedFullset', lk: 'xShipDoc.colCheckedFullset', w: 120, type: 'text' },
  { key: 'inboundNo', lk: 'xShipDoc.colInboundNo', w: 118, type: 'text' },
  { key: 'sfpMaterialDoc', lk: 'xShipDoc.colSfpMaterialDoc', w: 160, type: 'text' },
  { key: 'itemMaterialDoc', lk: 'xShipDoc.colItemMaterialDoc', w: 160, type: 'text' },
  { key: 'remark', lk: 'xShipDoc.colRemark', w: 220, type: 'text' },
];

/* What a PUT may carry. The screen edits `sfpSeq` and `unitPrice` in the cell the reader is looking
   at, but the server stores the typed value beside SAP's rather than over it, so the two are renamed
   on the way out — SAP's own figure has to survive being corrected. */
/* The quantity plain (no thousands mark), as Excel joins it; the date as the row prints it. */
function checkingOf(l) {
  const q = l.qty === null || l.qty === undefined || l.qty === '' ? '' : String(Number(l.qty));
  return `${l.sapCode || ''} & ${q} & ${l.exfDate || ''}`;
}

const SENT_AS = { sfpSeq: 'sfpSeqManual', itemSeq: 'itemSeqManual', unitPrice: 'unitPriceManual' };
const EDITABLE = COLUMNS.filter((c) => c.type).map((c) => c.key);
const EDITABLE_SET = new Set(EDITABLE);
/* Where SAP's half ends and the team's begins — the boundary the Columns menu draws a dot on. */
const TYPED_AFTER_ARRIVAL = new Set(['salesInvoice', 'inboundDelivery', 'blAwb', 'checkedFullset',
  'inboundNo', 'sfpMaterialDoc', 'itemMaterialDoc', 'remark']);

const GETTERS = Object.fromEntries(COLUMNS.map((c) => [
  c.key, c.get || (c.num ? (r) => Number(r[c.key] ?? 0) : (r) => r[c.key] || ''),
]));

/* THE GUIDE (Max 01/11: every module has one). The ladder of steps is the way into the screen, so the tour starts by
   putting it back on the Document view; the table and the export buttons belong to that view. */
const TOUR = [
  { selector: '.ssd-steps', title: 'tourShipDoc.stepsT', body: 'tourShipDoc.stepsB' },
  { selector: '.ssd-meta', title: 'tourShipDoc.metaT', body: 'tourShipDoc.metaB' },
  { selector: '.ssd-quarters', title: 'tourShipDoc.quarterT', body: 'tourShipDoc.quarterB' },
  { selector: '.cap-bar-right', title: 'tourShipDoc.toolsT', body: 'tourShipDoc.toolsB' },
  { selector: '.cap-scroll', title: 'tourShipDoc.tableT', body: 'tourShipDoc.tableB' },
];

const COL_STORE = 'hevn_shipdoc_columns_v1';
const FREEZE_STORE = 'hevn_shipdoc_freeze_v1';
/* Unticking everything would otherwise leave a table of row numbers and Edit buttons, which reads as
   broken rather than empty. */
const COL_LOCKED = new Set(['sfpCode']);
const readStore = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key) || '') ?? fallback; } catch (e) { return fallback; }
};
const writeStore = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ }
};

const n0 = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 }));
const n2 = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/* Quarters, because the document is built a quarter at a time — the guide says so in its first line
   ("copy paste theo 3 tháng / lần") and the workbook has a sheet per quarter. */
const QUARTERS = [['Jan-Mar', '01-01', '03-31', 'xShipDoc.q1'], ['Apr-Jun', '04-01', '06-30', 'xShipDoc.q2'],
  ['Jul-Sep', '07-01', '09-30', 'xShipDoc.q3'], ['Oct-Dec', '10-01', '12-31', 'xShipDoc.q4']];
const quarterOf = (d) => Math.floor(d.getMonth() / 3);

/* CHECK CHANGES — Max, 2026-10-14: "1 nút là check changes giống bên mastersheet để xem coi là có những
   thay đổi gì so với trước đó". The Master Sheet's own dialog (ChangeCheckDialog), told what a row of
   this document is called. It reads the WHOLE document, every quarter, as this screen shows it, and
   compares it with the last time this person marked it as checked — what a sync moved and what
   somebody typed. The values go through the same formatting as the table's cells. */
const fmtShipCell = (c, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (c === 'qty') return n0(v);
  if (c === 'unitPrice' || c === 'amount' || c === 'surcharge') return n2(v);
  return String(v);
};
/* Built per language (2026-10-21) and memoised on `t` where the dialog is opened. */
const shipDocChangesCfg = (t) => ({
  noun: t('xShipDoc.chgNoun'),
  reading: t('xShipDoc.chgReading'),
  groupField: 'vendorName',
  groupAll: t('xShipDoc.allVendors'),
  searchKeys: ['sfpPo', 'sapCode', 'description', 'customerPo', 'destination'],
  searchPlaceholder: t('xShipDoc.chgSearch'),
  columns: [
    { label: t('xShipDoc.colVendor'), cell: (x) => x.id.vendorName },
    { label: t('xShipDoc.colSfpPo'), cell: (x) => x.id.sfpPo },
    { label: t('xShipDoc.colSapCode'), cell: (x) => x.id.sapCode },
    { label: t('xShipDoc.colDescription'), cls: 'sp-chg-desc',
      cell: (x) => (x.id.description ? <>{x.id.description}{x.id.color ? <em> · {x.id.color}</em> : null}</> : null) },
    { label: t('xShipDoc.colCustomerPo'), cell: (x) => x.id.customerPo },
    { label: t('xShipDoc.colDestination'), cls: 'sp-chg-desc', cell: (x) => x.id.destination },
    { label: t('xShipDoc.colExfDate'), cell: (x) => x.id.exfDate },
  ],
  size: (x, fmt) => (
    <>
      {t('xShipDoc.pcs', { n: fmt('qty', x.id.qty) })}
      {x.id.amount !== null && x.id.amount !== undefined ? <> · {x.id.currency || ''} {fmt('amount', x.id.amount)}</> : null}
    </>
  ),
  fileName: 'Ship_doc_changes.xlsx',
});

/* When the window was last read from SAP — the time the document stands at. The sheet goes on
   changing after it (a sales order gains its customer PO the same evening), and the document does not
   follow until somebody syncs, so this is printed where the reader looks.
   THE STORED VALUE IS ALREADY VIETNAM TIME: the sync stamps it with GETDATE() on a database server
   that runs at +07, and the driver hands it over labelled UTC. So it is printed AS STORED (timeZone
   UTC) — converting it to Asia/Ho_Chi_Minh added seven hours, which is how the Source step's old card
   came to say 21 Sept 14:31 for a sync run at 07:31. Same trap as the GTM stamps. */
const syncLabel = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', {
  timeZone: 'UTC', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '');

export default function SapShipDoc() {
  /* `functions` is a list of KEYS only — the level lives in `permissions`, which is
     { key: 'view' | 'edit' }. Reading the level off `functions` would show Sync and the pencil to
     someone with view, and the server would then 403 every click. */
  const { permissions } = useAuth();
  const mayEdit = (permissions || {}).sap_ship_doc === 'edit';
  const { t } = useTranslation();
  const changesCfg = useMemo(() => shipDocChangesCfg(t), [t]);

  const today = new Date();
  const [range, setRange] = useState(() => {
    const y = today.getFullYear();
    const [, from, to] = QUARTERS[quarterOf(today)];
    return { from: `${y}-${from}`, to: `${y}-${to}` };
  });
  const [lines, setLines] = useState([]);
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [filters, setFilters] = useState({ vendor: '', search: '' });
  const [colFilters, setColFilters] = useState({});
  const [sourceRead, setSourceRead] = useState(0);     // the toolbar's reload, re-reading step 1
  const [openFilter, setOpenFilter] = useState(null);
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [colVisible, setColVisible] = useState(() => readStore(COL_STORE, {}));
  const [freezeThrough, setFreezeThrough] = useState(() => readStore(FREEZE_STORE, ''));
  const [colMenu, setColMenu] = useState(false);
  const [xlsMenu, setXlsMenu] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  /* WHICH STEP THE READER IS ON. The document used to be the only view, which is why it arrived as
     527 flagged rows with nowhere to start (Max, 2026-09-13). The steps are the same rows seen as
     three decisions; `output` is the document itself and stays reachable at any time. */
  const [view, setView] = useState('output');

  /* One confirm can answer 90 rows, so the server hands back the rows it changed and they are merged
     by id rather than the whole window being reloaded — a reload here would lose the reader's place
     in a list of 527. */
  const mergeLines = useCallback((changed) => {
    if (!changed.length) return;
    const byId = new Map(changed.map((l) => [l.id, l]));
    setLines((prev) => prev.map((l) => byId.get(l.id) || l));
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams({ from: range.from, to: range.to });
      const res = await fetch(`${API}?${qs}`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || i18n.t('xShipDoc.loadFailed', { status: res.status }));
      setLines(data.lines || []);
      setOptions(data.options || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true); setError(''); setNote('');
    try {
      const res = await fetch(`${API}/sync`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: range.from, to: range.to }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xShipDoc.syncFailed', { status: res.status }));
      const s = data.stats || {};
      setNote(t('xShipDoc.syncNote', {
        lines: n0(s.sfpLines), inserted: n0(data.inserted), updated: n0(data.updated), unpaired: n0(s.unpaired),
      }));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const exportTo = (mode) => {
    const qs = new URLSearchParams({ from: range.from, to: range.to, mode });
    if (filters.vendor) qs.set('vendor', filters.vendor);
    window.location.href = `${API}/export?${qs}`;
  };

  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => COL_LOCKED.has(c.key) || colVisible[c.key] !== false),
    [colVisible],
  );
  const setColumns = (next) => { setColVisible(next); writeStore(COL_STORE, next); };
  const hiddenCount = COLUMNS.length - visibleColumns.length;
  const setFreeze = (key) => { setFreezeThrough(key); writeStore(FREEZE_STORE, key); };

  const freezeIdx = visibleColumns.findIndex((c) => c.key === freezeThrough);
  const frozenCount = freezeIdx >= 0 ? freezeIdx + 1 : 0;
  const [tableRef, lefts] = useFrozenColumns(1 + frozenCount, [visibleColumns, frozenCount]);
  const tableWidth = NO_W + ACT_W + visibleColumns.reduce((a, c) => a + c.w, 0);

  /* THE GUIDE'S PIVOT CHECK, done for the reader. Its whole point is "lấy số lượng thực tế của PO trừ
     cho số lượng của mình tìm ra — nếu nó bằng 0 thì nó khớp": every ITEM line drawing on one SFP line
     must add back up to that line's PO quantity. Computed over ALL rows in the window, never the
     filtered ones — a subtotal that moves when you filter is not a reconciliation. */
  const reconciliation = useMemo(() => {
    const sums = new Map();
    const target = new Map();
    lines.forEach((l) => {
      if (!l.itemPo) return;
      const k = `${l.sfpPo}|${l.sfpSeqSap}`;
      sums.set(k, (sums.get(k) || 0) + (Number(l.qty) || 0));
      target.set(k, Number(l.sfpQty) || 0);
    });
    const off = new Set();
    sums.forEach((v, k) => { if (Math.abs(v - (target.get(k) || 0)) > 0.001) off.add(k); });
    return { groups: sums.size, off };
  }, [lines]);
  const isOffBalance = (l) => l.itemPo && reconciliation.off.has(`${l.sfpPo}|${l.sfpSeqSap}`);

  /* What each step still owes. One computation for the bar, the lists and the warning on the export,
     so they cannot disagree with each other. */
  /* What the Destination tab has left to do, counted off the rows on screen rather than off the
     alias table: a destination whose English name is in place is no longer printed in Korean, so the
     button goes to zero without asking the server a second question. The test is the script, not a
     list of values — a Korean name nobody has seen yet still has to be counted. */
  const koreanDests = useMemo(() => new Set(lines
    .map((l) => l.destination)
    .filter((d) => /[\uAC00-\uD7A3\u1100-\u11FF\u3130-\u318F]/.test(String(d || '')))).size,
  [lines]);

  const steps = useMemo(() => {
    const sfp = lines.filter(needsSfp).length;
    const item = lines.filter(needsItem).length;
    const qty = qtyGroups(lines).filter((g) => Math.abs(g.diff) >= 0.001 && !g.checked).length;
    return { sfp, item, qty, total: sfp + item + qty };
  }, [lines]);

  /* The vendor picker and the search box apply to the steps too — somebody works through one vendor
     at a time. The flag dropdown that sat beside them is gone (Max, 2026-10-20: "tối ưu và tối giản"):
     its three choices were steps 2, 3 and 4 said a second time, and the document's own column funnels
     still narrow it any way at all. */
  const stepRows = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return lines.filter((l) => (!filters.vendor || l.vendorName === filters.vendor)
      && (!q || [l.sfpCode, l.sapCode, l.description, l.sfpPo, l.itemPo, l.customerPo, l.color, l.destination]
        .some((v) => String(v || '').toLowerCase().includes(q))));
  }, [lines, filters.vendor, filters.search]);

  const toolbarRows = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return lines.filter((l) => (
      (!filters.vendor || l.vendorName === filters.vendor)
      && (!q || [l.sfpCode, l.sapCode, l.description, l.sfpPo, l.itemPo, l.customerPo, l.color, l.destination]
        .some((v) => String(v || '').toLowerCase().includes(q)))
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, filters, reconciliation]);

  const filtered = useMemo(() => {
    const afterCols = Object.keys(colFilters).length
      ? toolbarRows.filter((r) => rowPassesColumnFilters(r, colFilters, GETTERS)) : toolbarRows;
    return sortRows(afterCols, sort, GETTERS);
  }, [toolbarRows, colFilters, sort]);

  /* Quang's duplicate check, over EVERY row of the window — his conditional format reads the whole
     column, and a twin hidden by a vendor filter is still a twin. A row with no SAP code has no key. */
  const checkingCount = useMemo(() => {
    const m = new Map();
    lines.forEach((l) => { if (l.sapCode) { const k = checkingOf(l); m.set(k, (m.get(k) || 0) + 1); } });
    return m;
  }, [lines]);
  const dupValues = useMemo(() => [...checkingCount.entries()].filter(([, n]) => n > 1).map(([k]) => k),
    [checkingCount]);
  const dupRows = dupValues.reduce((a, k) => a + checkingCount.get(k), 0);

  const lastSync = useMemo(() => lines.reduce((a, l) => (l.syncedAt && l.syncedAt > a ? l.syncedAt : a), ''), [lines]);

  /* THE SUBTOTAL ROW — Quang, 2026-10-01: "please add a subtotal here, showing both the total
     quantity and total amount". Over the rows the filters leave, like the cards above, and ONE ROW PER
     CURRENCY: a window can hold USD and KRW POs, and one amount over both is a number with no unit.
     Rows with no price yet get their own line with a dash for money, so 0.00 cannot read as free. */
  const subtotals = useMemo(() => {
    const by = new Map();
    filtered.forEach((l) => {
      const cur = l.currency || '';
      if (!by.has(cur)) by.set(cur, { currency: cur, rows: 0, qty: 0, amount: 0, priced: 0 });
      const g = by.get(cur);
      g.rows += 1;
      g.qty += Number(l.qty) || 0;
      if (l.amount !== null && l.amount !== undefined) { g.amount += Number(l.amount); g.priced += 1; }
    });
    return [...by.values()].sort((a, b) => (a.currency ? 0 : 1) - (b.currency ? 0 : 1) || b.amount - a.amount);
  }, [filtered]);

  const [scrollBox, win] = useRowWindow({
    rowHeight: ROW_H,
    total: filtered.length,
    resetKey: `${sort.key}${sort.dir}|${JSON.stringify(filters)}|${JSON.stringify(colFilters)}`,
  });
  const visible = win.windowed ? filtered.slice(win.first, win.first + win.count) : filtered;
  /* The table reaches the bottom of the window whatever sits above it (a sync note, an error, a
     toolbar that wraps on a narrower screen) — a fixed calc() was right for one header only. */
  useFillHeight(scrollBox, [view, filtered.length > 0, !!note, !!error, !!editError, !!edit]);
  const filtersApply = ['step2', 'step3', 'step4', 'output'].includes(view);
  const spacerSpan = visibleColumns.length + 2;

  const hiddenEditable = EDITABLE.filter((k) => !visibleColumns.some((c) => c.key === k));
  const firstTypedVisible = EDITABLE.find((k) => visibleColumns.some((c) => c.key === k)) || '';
  const startEdit = (line) => {
    setEditError('');
    setEdit({
      id: line.id,
      /* Seeded with what the row SHOWS, so opening the editor and pressing Enter changes nothing —
         for the two overridable columns that is the typed value if there is one, SAP's if there is
         not, which is exactly what the cell beside the caret reads. */
      form: Object.fromEntries(EDITABLE.map((k) => [k, line[k] === null || line[k] === undefined ? '' : String(line[k])])),
    });
  };
  const setField = (k) => (e) => setEdit((p) => ({ ...p, form: { ...p.form, [k]: e.target.value } }));
  const saveEdit = async () => {
    if (!edit || saving) return;
    setSaving(true); setEditError('');
    try {
      const body = {};
      Object.entries(edit.form).forEach(([k, v]) => { body[SENT_AS[k] || k] = v; });
      const res = await fetch(`${API}/${edit.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xShipDoc.saveFailed', { status: res.status }));
      setLines((prev) => prev.map((l) => (l.id === data.line.id ? data.line : l)));
      setEdit(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const editKeys = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
    if (e.key === 'Escape') { e.preventDefault(); setEdit(null); setEditError(''); }
  };

  const thProps = (key) => ({
    sortKey: key,
    sort,
    onSort: (k) => setSort((p) => (p.key === k ? { key: k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' })),
    filterValues: openFilter?.key === key ? distinctColumnValues(toolbarRows, key, colFilters, GETTERS) : undefined,
    filterSelected: colFilters[key],
    filterOpen: openFilter?.key === key,
    filterAnchor: openFilter?.key === key ? openFilter.anchor : null,
    onOpenFilter: (k, anchor) => setOpenFilter((p) => (p && p.key === k ? null : { key: k, anchor })),
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (k, values, allValues) => {
      setColFilters((prev) => {
        const next = { ...prev };
        const isAll = allValues && allValues.length && values.length >= allValues.length;
        if (!values.length || isAll) delete next[k]; else next[k] = values;
        return next;
      });
      setOpenFilter(null);
    },
  });

  const cell = (l, c) => {
    if (c.key === 'checking') {
      const k = checkingOf(l);
      const n = l.sapCode ? (checkingCount.get(k) || 0) : 0;
      return (
        <span className={`ssd-check ${n > 1 ? 'dup' : ''}`}
          title={n > 1 ? t('xShipDoc.dupHint', { n }) : undefined}>
          {k}
        </span>
      );
    }
    if (c.key === 'qty') return n0(l.qty);
    if (c.key === 'unitPrice' || c.key === 'amount' || c.key === 'surcharge') return n2(l[c.key]);
    /* The one number SAP does not settle. Marked where it is read rather than in a legend, and the
       mark goes the moment somebody types over it. */
    if (c.key === 'sfpSeq') {
      const proposed = l.sfpSeqAmbiguous && !l.sfpSeqManual;
      return (
        <span className={proposed ? 'ssd-proposed' : ''}
          title={proposed ? t('xShipDoc.sfpSeqProposed') : (l.sfpSeqManual ? t('xShipDoc.typedSapProposed', { seq: l.sfpSeqSap }) : undefined)}
        >
          {l.sfpSeq || '—'}
          {proposed && <CircleAlert size={11} />}
        </span>
      );
    }
    if (c.key === 'itemSeq') {
      const proposed = l.itemSeqAmbiguous && !l.itemSeqManual;
      if (!l.itemSeq) return <span className="ssd-missing" title={t('xShipDoc.itemSeqMissing')}>—</span>;
      return (
        <span className={proposed ? 'ssd-proposed' : ''}
          title={proposed ? t('xShipDoc.itemSeqProposed') : (l.itemSeqManual ? t('xShipDoc.typedSapProposed', { seq: l.itemSeqSap }) : undefined)}
        >
          {l.itemSeq}
          {proposed && <CircleAlert size={11} />}
        </span>
      );
    }
    if (c.key === 'itemPo' && !l.itemPo) {
      return <span className="ssd-missing" title={t('xShipDoc.itemPoMissing')}>—</span>;
    }
    return l[c.key] || '—';
  };

  const clearAll = () => {
    setFilters({ vendor: '', search: '' });
    setColFilters({});
  };

  return (
    <div className="sp-page cap-page ssd-page">
      <Header />
      {/* .sp-main carries the 270px sidebar offset and the page padding — a shell of my own had
          neither and the table sat under an open sidebar (Max, 2026-09-09). */}
      <div className="sp-main">
        <div className="mh-titlerow">
        <div className="cap-head ssd-head">
          {/* The sentence that sat under the title came off (Max, 2026-10-20, boxed in red with the rest
              of what made the page busy); it rides on the title's hover for whoever wonders. */}
          <h2 title={t('xShipDoc.titleHint')}>
            {t('xShell.sapShipDoc')}
          </h2>
        </div>
        <ModuleHelp steps={TOUR} tourKey="sapShipDoc" ready={!loading} onBeforeTour={() => setView('output')} />
        </div>

        {!!error && <div className="sp-error"><AlertTriangle size={14} /> {error}</div>}
        {!!note && <div className="ssd-note">{note}</div>}

        {/* THE LADDER, as one strip of tabs — Max, 2026-10-14: "làm cho nó lean lại … quá nhiều card".
            It used to sit under a row of six figure cards (rows, quantity, amount, seq to confirm, no
            ITEM PO, quantity check); three of those were these same counts said twice, and the other
            three now ride on the summary line above the table. Counts come from the same predicates
            the lists use, so a step that says 0 cannot still hold rows. Not a gate: every step is
            reachable in any order, and so is the document — Max, 2026-09-13, "không chặn, chỉ cảnh báo". */}
        <div className="ssd-steps" role="tablist">
          {[
            ['step1', t('xShipDoc.step1'), 0, t('xShipDoc.step1Hint')],
            ['step2', t('xShipDoc.step2'), steps.sfp, t('xShipDoc.step2Hint')],
            ['step3', t('xShipDoc.step3'), steps.item, t('xShipDoc.step3Hint')],
            ['step4', t('xShipDoc.step4'), steps.qty, t('xShipDoc.step4Hint')],
            /* Not numbered, because it is not a rung of the ladder: the four steps decide which SAP
               line a row belongs to, this decides how one of its columns is worded. It sits before
               the document because that is when the wording matters. */
            ['dest', t('xShipDoc.stepDest'), koreanDests, t('xShipDoc.stepDestHint')],
            ['output', t('xShipDoc.stepOutput'), 0, t('xShipDoc.stepOutputHint')],
          ].map(([key, label, left, hint]) => {
            const counted = key !== 'step1' && key !== 'output';
            // What the badge says, in words: the tab's hover and its aria-label.
            const said = !counted ? '' : (left
              ? t(key === 'dest' ? 'xShipDoc.stepKorean' : 'xShipDoc.stepLeft', { n: n0(left) })
              : t('xShipDoc.stepDone'));
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={view === key}
                className={`ssd-step-btn ${view === key ? 'on' : ''} ${counted ? (left ? 'has-work' : 'clear') : ''}`}
                onClick={() => setView(key)}
                title={said ? `${hint} — ${said}` : hint}
              >
                {label}
                {counted && (
                  <span className="ssd-step-badge" aria-label={said}>
                    {left ? n0(left) : <Check size={11} />}
                  </span>
                )}
              </button>
            );
          })}
          {/* THE SUMMARY RIDES ON THE LADDER'S ROW (Max, 2026-10-20: "chưa clean và lean lắm"). It was a
              line of its own under the toolbar repeating what the page already says elsewhere — the
              pieces and the amount are the subtotal row at the foot of the table, the unchecked counts
              are the badges on these tabs. What only it says stays: how many rows are on screen, and
              when the document was last read from SAP (the fact that explained the "75" of 14/10). */}
          <div className="ssd-meta">
            {view === 'output' && lines.length > 0 && (
              <span>
                <Trans t={t} i18nKey={filtered.length !== lines.length ? 'xShipDoc.metaRowsOf' : 'xShipDoc.metaRows'}
                  values={{ n: n0(filtered.length), total: n0(lines.length) }} components={{ b: <strong /> }} />
              </span>
            )}
            {view === 'output' && dupRows > 0 && (
              <button type="button" className="ssd-linkbtn ssd-dup-link"
                title={t('xShipDoc.dupLinkHint')}
                onClick={() => setColFilters((p) => ({ ...p, checking: dupValues }))}>
                <CircleAlert size={12} /> {t('xShipDoc.dupLink', { n: n0(dupRows) })}
              </button>
            )}
            <span className="ssd-meta-sync" title={t('xShipDoc.syncHint')}>
              {lastSync ? t('xShipDoc.readFromSap', { at: syncLabel(lastSync) }) : t('xShipDoc.notReadYet')}
            </span>
          </div>
        </div>

        <div className="cap-bar">
          <div className="ssd-quarters">
            {QUARTERS.map(([label, from, to, lk]) => {
              const y = range.from.slice(0, 4);
              const on = range.from.endsWith(from) && range.to.endsWith(to);
              return (
                <button
                  key={label}
                  type="button"
                  className={`sp-btn ${on ? 'cap-on' : ''}`}
                  onClick={() => setRange({ from: `${y}-${from}`, to: `${y}-${to}` })}
                >
                  {t(lk)}
                </button>
              );
            })}
          </div>
          <input
            type="date"
            value={range.from}
            onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))}
          />
          <span className="ssd-dash">–</span>
          <input
            type="date"
            value={range.to}
            onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))}
          />
          {/* Each control is drawn only where it acts. Source and Destination read the date window and
              nothing else, so a vendor picker there would narrow nothing while looking as if it did. */}
          {filtersApply && (
            <>
              <select value={filters.vendor} onChange={(e) => setFilters((p) => ({ ...p, vendor: e.target.value }))}>
                <option value="">{t('xShipDoc.allVendors')}</option>
                {(options.vendors || []).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <input
                value={filters.search}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                placeholder={t('xShipDoc.searchPh')}
              />
              <button type="button" className="sp-btn ssd-icon" onClick={clearAll} title={t('xShipDoc.clearFilters')} aria-label={t('xShipDoc.clearFilters')}>
                <FilterX size={13} />
              </button>
            </>
          )}

          <div className="cap-bar-right">
            {view === 'output' && (
            <>
            <div className="sp-colmenu-wrap">
              {/* An icon, its name on the hover: the toolbar is one row at 1,670px only if the two buttons
                  every page already knows (Columns, Clear) give up their words. The hidden count stays. */}
              <button type="button" className="sp-btn ssd-icon" onClick={() => setColMenu((v) => !v)}
                title={hiddenCount ? t('xShipDoc.columnsHidden', { n: hiddenCount }) : t('xShipDoc.columns')} aria-label={t('xShipDoc.columns')}>
                <Columns3 size={13} />
                {hiddenCount > 0 && <em>&nbsp;{hiddenCount}</em>}
              </button>
              {colMenu && (
                <>
                  {/* Click-away, not blur: a blur fires before the checkbox it was heading for gets
                      the click, which closed Master Sheet's menu on every tick. */}
                  <div className="sp-colmenu-back" onClick={() => setColMenu(false)} />
                  <div className="sp-colmenu cap-colmenu">
                    <div className="sp-colmenu-h">
                      {t('xShipDoc.showColumns')}
                      <button type="button" onClick={() => setColumns({})}>{t('xShipDoc.showAll')}</button>
                    </div>
                    <div className="cap-colmenu-note">
                      <Pin size={11} /> {t('xShipDoc.pinNote')}
                      {frozenCount > 0 && (
                        <button type="button" onClick={() => setFreeze('')}>{t('xShipDoc.unfreeze')}</button>
                      )}
                    </div>
                    {COLUMNS.map((c) => {
                      const locked = COL_LOCKED.has(c.key);
                      const shown = locked || colVisible[c.key] !== false;
                      return (
                        <label key={c.key} className={locked ? 'locked' : ''}>
                          <input
                            type="checkbox"
                            checked={shown}
                            disabled={locked}
                            onChange={(e) => setColumns({ ...colVisible, [c.key]: e.target.checked })}
                          />
                          <span className={TYPED_AFTER_ARRIVAL.has(c.key) ? 'cap-typed-dot' : 'cap-sys-dot'} />
                          <span className="cap-colname">{t(c.lk)}</span>
                          <button
                            type="button"
                            className={`cap-pin ${freezeThrough === c.key ? 'on' : ''}`}
                            title={shown ? t('xShipDoc.freezeHere') : t('xShipDoc.freezeHidden')}
                            disabled={!shown}
                            onClick={(e) => {
                              e.preventDefault();
                              setFreeze(freezeThrough === c.key ? '' : c.key);
                            }}
                          >
                            <Pin size={12} />
                          </button>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <button type="button" className="sp-btn" onClick={() => setChangesOpen(true)}
              title={t('xShipDoc.checkChangesHint')}>
              <ListChecks size={13} /> {t('xShipDoc.checkChanges')}
            </button>
            {/* ONE BUTTON, TWO FILES. The two exports differ only in how the sheet is split, which is
                a question asked after "export", not a second thing to read on the toolbar. */}
            <div className="sp-colmenu-wrap">
              <button type="button" className="sp-btn" onClick={() => setXlsMenu((v) => !v)} disabled={!filtered.length}>
                <Download size={13} /> Excel <ChevronDown size={12} />
              </button>
              {xlsMenu && (
                <>
                  <div className="sp-colmenu-back" onClick={() => setXlsMenu(false)} />
                  <div className="sp-colmenu ssd-xlsmenu">
                    <button type="button" onClick={() => { setXlsMenu(false); exportTo('single'); }}>{t('xShipDoc.xlsOne')}</button>
                    <button type="button" onClick={() => { setXlsMenu(false); exportTo('vendor'); }}>{t('xShipDoc.xlsPerVendor')}</button>
                  </div>
                </>
              )}
            </div>
            </>
            )}
            {mayEdit && (
              <button type="button" className="sp-btn primary" onClick={runSync} disabled={syncing}>
                <DownloadCloud size={13} /> {syncing ? t('xShipDoc.readingSap') : t('xShipDoc.syncFromSap')}
              </button>
            )}
            <button type="button" className="sp-btn ssd-icon"
              onClick={() => { if (view === 'step1') setSourceRead((v) => v + 1); load(); }}
              disabled={loading}
              title={view === 'step1' ? t('xShipDoc.readAgain') : t('xShipDoc.reload')} aria-label={t('xShipDoc.reload')}>
              <RefreshCw size={13} className={loading ? 'ssd-spin' : ''} />
            </button>
          </div>
        </div>

        {!!editError && <div className="sp-error"><AlertTriangle size={14} /> {editError}</div>}
        {!!edit && hiddenEditable.length > 0 && (
          <div className="cap-notice">
            {t('xShipDoc.hiddenEditable', { count: hiddenEditable.length })}
            <button
              type="button"
              onClick={() => setColumns(Object.fromEntries(
                Object.entries(colVisible).filter(([k]) => !EDITABLE_SET.has(k)),
              ))}
            >
              {t('xShipDoc.showThem')}
            </button>
          </div>
        )}

        {view === 'step1' && <SourceStep from={range.from} to={range.to} readAgain={sourceRead} />}
        {view === 'step2' && (
          <SeqStep which="sfp" lines={stepRows} canEdit={mayEdit} onLines={mergeLines}
            note={t('xShipDoc.step2Note')} />
        )}
        {view === 'step3' && (
          <SeqStep which="item" lines={stepRows} canEdit={mayEdit} onLines={mergeLines}
            note={t('xShipDoc.step3Note')} />
        )}
        {view === 'step4' && <QtyStep lines={stepRows} canEdit={mayEdit} onLines={mergeLines} />}
        {view === 'dest' && <DestinationStep canEdit={mayEdit} onApplied={load} />}

        <div className="cap-scroll" ref={scrollBox} style={view === 'output' ? undefined : { display: 'none' }}>
          <table className="cap-table" ref={tableRef} style={{ width: tableWidth }}>
            <colgroup>
              <col style={{ width: NO_W }} />
              {visibleColumns.map((c) => <col key={c.key} style={{ width: c.w }} />)}
              <col style={{ width: ACT_W }} />
            </colgroup>
            <thead>
              <tr>
                <th className="cap-no sticky-col" style={{ left: lefts[0] || 0 }}>#</th>
                {visibleColumns.map((c, i) => {
                  const frozen = i < frozenCount;
                  return (
                    <SortTh
                      key={c.key}
                      label={t(c.lk)}
                      {...thProps(c.key)}
                      className={[
                        c.num ? 'cap-num' : '',
                        frozen ? 'sticky-col' : '',
                        frozen && i === frozenCount - 1 ? 'cap-freeze-edge' : '',
                      ].filter(Boolean).join(' ')}
                      style={frozen ? { left: lefts[i + 1] } : undefined}
                    />
                  );
                })}
                <th className="cap-act">{t('xShipDoc.thAction')}</th>
              </tr>
            </thead>
            <tbody>
              {win.windowed && win.padTop > 0 && (
                <tr className="cap-spacer" style={{ height: win.padTop }}><td colSpan={spacerSpan} /></tr>
              )}
              {visible.map((l, i) => {
                const editingThis = edit && edit.id === l.id;
                return (
                  <tr
                    key={l.id}
                    className={`${editingThis ? 'cap-editing' : ''} ${isOffBalance(l) ? 'ssd-off' : ''}`}
                  >
                    <td className="cap-no sticky-col" style={{ left: lefts[0] || 0 }}>
                      {(win.windowed ? win.first : 0) + i + 1}
                    </td>
                    {visibleColumns.map((c, ci) => {
                      const frozen = ci < frozenCount;
                      const cls = [
                        c.num ? 'cap-num' : '',
                        frozen ? 'sticky-col' : '',
                        frozen && ci === frozenCount - 1 ? 'cap-freeze-edge' : '',
                      ].filter(Boolean).join(' ');
                      if (editingThis && EDITABLE_SET.has(c.key)) {
                        return (
                          <td key={c.key} className={`${cls} cap-cell-edit`} style={frozen ? { left: lefts[ci + 1] } : undefined}>
                            <input
                              type={c.type === 'number' ? 'number' : 'text'}
                              step={c.type === 'number' ? 'any' : undefined}
                              value={edit.form[c.key]}
                              onChange={setField(c.key)}
                              onKeyDown={editKeys}
                              /* eslint-disable-next-line jsx-a11y/no-autofocus */
                              autoFocus={c.key === firstTypedVisible}
                            />
                          </td>
                        );
                      }
                      return (
                        <td
                          key={c.key}
                          className={cls}
                          style={frozen ? { left: lefts[ci + 1] } : undefined}
                          title={c.key === 'description' || c.key === 'remark' ? (l[c.key] || '') : undefined}
                        >
                          {cell(l, c)}
                        </td>
                      );
                    })}
                    <td className="cap-act">
                      {editingThis ? (
                        <span className="cap-rowbtns">
                          <button type="button" className="sp-btn tiny primary" title={t('xShipDoc.saveEnter')} disabled={saving} onClick={saveEdit}>
                            <Check size={12} />
                          </button>
                          <button type="button" className="sp-btn tiny" title={t('xShipDoc.cancelEsc')} onClick={() => { setEdit(null); setEditError(''); }}>
                            <X size={12} />
                          </button>
                        </span>
                      ) : mayEdit && (
                        <button type="button" className="sp-btn tiny" title={t('xShipDoc.editHint')} onClick={() => startEdit(l)}>
                          <Pencil size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {win.windowed && win.padBottom > 0 && (
                <tr className="cap-spacer" style={{ height: win.padBottom }}><td colSpan={spacerSpan} /></tr>
              )}
              {!filtered.length && !loading && (
                <tr>
                  <td colSpan={spacerSpan} className="cap-empty">
                    {lines.length
                      ? t('xShipDoc.noMatch')
                      : t('xShipDoc.emptyWindow')}
                  </td>
                </tr>
              )}
            </tbody>
            {!!filtered.length && (
              <tfoot className="ssd-subtotals">
                {subtotals.map((g) => (
                  <tr key={g.currency || 'none'}>
                    <td className="cap-no sticky-col" style={{ left: lefts[0] || 0 }}>Σ</td>
                    {visibleColumns.map((c, ci) => {
                      const frozen = ci < frozenCount;
                      const cls = [
                        c.num ? 'cap-num' : '',
                        frozen ? 'sticky-col' : '',
                        frozen && ci === frozenCount - 1 ? 'cap-freeze-edge' : '',
                      ].filter(Boolean).join(' ');
                      let v = '';
                      /* The label sits in whichever column is first — Quang hides Vendor, which leaves
                         Destination at 110px — so it stays short and the row count rides on the hover. */
                      if (ci === 0) {
                        v = (
                          <span className="ssd-subtotal-label" title={t('xShipDoc.rows', { count: g.rows, n: n0(g.rows) })}>
                            {g.currency ? t('xShipDoc.total', { cur: g.currency }) : t('xShipDoc.totalNoPrice')}
                          </span>
                        );
                      } else if (c.key === 'qty') v = n0(g.qty);
                      else if (c.key === 'amount') v = g.currency && g.priced ? n2(g.amount) : '—';
                      return (
                        <td key={c.key} className={cls} style={frozen ? { left: lefts[ci + 1] } : undefined}>{v}</td>
                      );
                    })}
                    <td className="cap-act" />
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {changesOpen && (
        <ChangeCheckDialog base={`${API}/changes`} fmt={fmtShipCell} cfg={changesCfg}
          onClose={() => setChangesOpen(false)} />
      )}
    </div>
  );
}
